import type { LaneData, SignalData, TileTraffic } from '../gen/traffic/network';

/**
 * Traffic simulation, pure (no three.js), in render space (metres, y up) (DECISIONS "Traffic").
 * - The network is assembled from the tiles' lanes (TileTraffic), joined at nodes by key, and
 *   connectors across junctions and bends are cubic curves built on demand.
 * - Cars follow the Intelligent Driver Model: acceleration from the desired speed and the gap to what's
 *   ahead (the next car on this segment or the next one, a red/yellow light's stop line, a stop sign
 *   until the car has stopped and the junction is clear, or an obstacle like the player).
 * - At the end of a lane a car turns onto a random lane leaving that node (not back the way it came,
 *   unless it's a dead end).
 * - Lights: two phases per junction, the approaches grouped by direction; offsets hashed per node.
 */

export interface Seg {
  id: string;
  /** Points (x, y, z) and the cumulative length at each. */
  pts: Float32Array;
  cum: Float32Array;
  length: number;
  /** Cars on the segment, sorted by s. */
  cars: SimCar[];
  lane: Lane | null;
  /** Connectors: the junction node they cross, and where they lead. */
  node?: string;
  to?: Lane;
}

export interface Lane extends Seg {
  from: string;
  to: undefined;
  end: string;
  stopAtEnd: boolean;
  index: number;
  speed: number;
  tile: string;
  /** Direction of travel at the lane's end (x, z), for the light phase. */
  dir: [number, number];
}

export interface SimCar {
  id: number;
  seg: Seg;
  s: number;
  v: number;
  /** The lane after the current one, chosen before reaching the junction. */
  next: Lane | null;
  body: number;
  color: [number, number, number];
  /** Seconds stopped at a stop sign; set once allowed through. */
  waited: number;
  cleared: boolean;
  /** Seconds held up (nearly stopped) by an obstacle: the player in the way. Drivers honk. */
  blocked: number;
  /** Acceleration at the last step (m/s²): brake lights. */
  a: number;
  /** Nose-up pitch (radians) from the lane's slope under the axles, and the height (m) to add to the
   * pose so both axles sit on the road. */
  pitch: number;
  lift: number;
  dead: boolean;
  /** Poses at the last two steps (x, y, z, heading about y). */
  prev: [number, number, number, number];
  cur: [number, number, number, number];
}

export const CAR = { length: 4.6, accel: 1.6, brake: 3, headway: 1.3, minGap: 2.2, turnSpeed: 6 };

interface NodeSignal {
  dirs: [number, number][];
  offset: number;
}

export type LightState = 'green' | 'yellow' | 'red';

/** Seconds: green, yellow, all red, per phase; two phases per cycle. */
export const LIGHT = { green: 13, yellow: 3, allRed: 1.5 };
const CYCLE = 2 * (LIGHT.green + LIGHT.yellow + LIGHT.allRed);

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) / 4294967296;
}

function polySeg(id: string, pts: Float32Array): Seg {
  const n = pts.length / 3;
  const cum = new Float32Array(n);
  for (let i = 1; i < n; i++) cum[i] = cum[i - 1] + Math.hypot(pts[i * 3] - pts[i * 3 - 3], pts[i * 3 + 1] - pts[i * 3 - 2], pts[i * 3 + 2] - pts[i * 3 - 1]);
  return { id, pts, cum, length: cum[n - 1], cars: [], lane: null };
}

/** Position and heading (about +y; 0 = +x, toward −z positive) at distance s along a segment. */
export function sampleSeg(seg: Seg, s: number, out: [number, number, number, number]): [number, number, number, number] {
  const { pts, cum } = seg;
  const n = cum.length;
  s = Math.max(0, Math.min(seg.length, s));
  let i = 1;
  while (i < n - 1 && cum[i] < s) i++;
  const t = (s - cum[i - 1]) / Math.max(1e-6, cum[i] - cum[i - 1]);
  const a = (i - 1) * 3;
  const b = i * 3;
  out[0] = pts[a] + (pts[b] - pts[a]) * t;
  out[1] = pts[a + 1] + (pts[b + 1] - pts[a + 1]) * t;
  out[2] = pts[a + 2] + (pts[b + 2] - pts[a + 2]) * t;
  out[3] = Math.atan2(-(pts[b + 2] - pts[a + 2]), pts[b] - pts[a]);
  return out;
}

const tmpA: [number, number, number, number] = [0, 0, 0, 0];
const tmpB: [number, number, number, number] = [0, 0, 0, 0];
/** Half a car's wheelbase (m). */
const AXLE = 1.35;

/** Set `out.pitch` and `out.lift` from the segment's height at the axles around `s`. */
export function slopeAt(seg: Seg, s: number, out: { pitch: number; lift: number }): void {
  const a = Math.max(0, s - AXLE);
  const b = Math.min(seg.length, s + AXLE);
  if (b - a < 0.5) {
    out.pitch = 0;
    out.lift = 0;
    return;
  }
  const yb = sampleSeg(seg, a, tmpA)[1];
  const yf = sampleSeg(seg, b, tmpB)[1];
  const yc = sampleSeg(seg, s, tmpA)[1];
  out.pitch = Math.atan2(yf - yb, b - a);
  out.lift = yb + ((yf - yb) * (s - a)) / (b - a) - yc;
}

export class TrafficNet {
  readonly lanes = new Map<string, Lane>();
  private readonly byFrom = new Map<string, Lane[]>();
  private readonly byTile = new Map<string, Lane[]>();
  private readonly signals = new Map<string, NodeSignal>();
  private readonly signalTiles = new Map<string, string[]>();
  private readonly conns = new Map<string, Seg>();
  /** Connectors by the lane they lead into (merges). */
  private readonly into = new Map<string, Seg[]>();
  time = 0;

  addTile(tile: string, data: TileTraffic): void {
    if (this.byTile.has(tile)) return;
    const list: Lane[] = [];
    for (const d of data.lanes) {
      if (this.lanes.has(d.id)) continue;
      const lane = this.makeLane(tile, d);
      this.lanes.set(d.id, lane);
      list.push(lane);
      const l = this.byFrom.get(d.from);
      if (l) l.push(lane);
      else this.byFrom.set(d.from, [lane]);
    }
    this.byTile.set(tile, list);
    const keys: string[] = [];
    for (const s of data.signals) this.addSignal(s, keys);
    this.signalTiles.set(tile, keys);
  }

  removeTile(tile: string): void {
    for (const lane of this.byTile.get(tile) ?? []) {
      this.lanes.delete(lane.id);
      const l = this.byFrom.get(lane.from);
      if (l) {
        const i = l.indexOf(lane);
        if (i >= 0) l.splice(i, 1);
        if (l.length === 0) this.byFrom.delete(lane.from);
      }
      for (const c of lane.cars) c.dead = true;
    }
    for (const [k, c] of this.conns) {
      if ((c.to && !this.lanes.has(c.to.id)) || !this.lanes.has(k.slice(0, k.indexOf('>')))) {
        for (const car of c.cars) car.dead = true;
        this.conns.delete(k);
        if (c.to) this.into.set(c.to.id, (this.into.get(c.to.id) ?? []).filter((x) => x !== c));
      }
    }
    this.byTile.delete(tile);
    for (const k of this.signalTiles.get(tile) ?? []) this.signals.delete(k);
    this.signalTiles.delete(tile);
  }

  get tiles(): IterableIterator<string> {
    return this.byTile.keys();
  }

  private makeLane(tile: string, d: LaneData): Lane {
    const seg = polySeg(d.id, Float32Array.from(d.pts));
    const n = seg.pts.length / 3;
    const dx = seg.pts[(n - 1) * 3] - seg.pts[(n - 2) * 3];
    const dz = seg.pts[(n - 1) * 3 + 2] - seg.pts[(n - 2) * 3 + 2];
    const l = Math.hypot(dx, dz) || 1;
    const lane = seg as Lane;
    Object.assign(lane, { from: d.from, end: d.to, stopAtEnd: d.stopAtEnd, index: d.index, speed: d.speed, tile, dir: [dx / l, dz / l] });
    lane.lane = lane;
    return lane;
  }

  private addSignal(s: SignalData, keys: string[]): void {
    let sig = this.signals.get(s.node);
    if (!sig) {
      sig = { dirs: [], offset: hashStr(s.node) * CYCLE };
      this.signals.set(s.node, sig);
      keys.push(s.node);
    }
    sig.dirs.push(s.dir);
  }

  hasSignal(node: string): boolean {
    return this.signals.has(node);
  }

  /** Light state for traffic arriving at `node` moving along `dir`. */
  light(node: string, dir: [number, number]): LightState {
    const sig = this.signals.get(node);
    if (!sig) return 'green';
    const d0 = sig.dirs[0];
    // Phase 0: parallel to the first approach (either way); phase 1: across it.
    const phase = Math.abs(dir[0] * d0[0] + dir[1] * d0[1]) > Math.SQRT1_2 ? 0 : 1;
    const t = (this.time + sig.offset) % CYCLE;
    const half = CYCLE / 2;
    const tp = phase === 0 ? t : (t + half) % CYCLE;
    if (tp < LIGHT.green) return 'green';
    if (tp < LIGHT.green + LIGHT.yellow) return 'yellow';
    return 'red';
  }

  /** Lanes leaving `node`, excluding the way back along `from`'s edge unless it's the only way. */
  exits(from: Lane): Lane[] {
    const all = this.byFrom.get(from.end) ?? [];
    const edge = from.id.slice(0, from.id.lastIndexOf('|', from.id.lastIndexOf('|') - 1));
    const out = all.filter((l) => !l.id.startsWith(edge + '|'));
    return out.length ? out : all;
  }

  /** The curve from the end of `a` to the start of `b` (cached). */
  connector(a: Lane, b: Lane): Seg {
    const key = `${a.id}>${b.id}`;
    let c = this.conns.get(key);
    if (c) return c;
    const n = a.pts.length / 3;
    const p0 = [a.pts[(n - 1) * 3], a.pts[(n - 1) * 3 + 1], a.pts[(n - 1) * 3 + 2]];
    const p3 = [b.pts[0], b.pts[1], b.pts[2]];
    const d = Math.hypot(p3[0] - p0[0], p3[2] - p0[2]);
    const bl = Math.hypot(b.pts[3] - b.pts[0], b.pts[5] - b.pts[2]) || 1;
    const t1 = [(b.pts[3] - b.pts[0]) / bl, (b.pts[5] - b.pts[2]) / bl];
    const k = d / 3;
    const p1 = [p0[0] + a.dir[0] * k, p0[2] + a.dir[1] * k];
    const p2 = [p3[0] - t1[0] * k, p3[2] - t1[1] * k];
    const steps = Math.max(2, Math.ceil(d / 2));
    const pts = new Float32Array((steps + 1) * 3);
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const u = 1 - t;
      pts[i * 3] = u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0];
      pts[i * 3 + 1] = p0[1] + (p3[1] - p0[1]) * t;
      pts[i * 3 + 2] = u * u * u * p0[2] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[2];
    }
    c = polySeg(key, pts);
    c.node = a.end;
    c.to = b;
    if (c.length < 0.01) {
      c.length = 0.01;
      c.cum[c.cum.length - 1] = 0.01;
    }
    this.conns.set(key, c);
    const l = this.into.get(b.id);
    if (l) l.push(c);
    else this.into.set(b.id, [c]);
    return c;
  }

  /** Connectors leading into `lane`. */
  mergingInto(lane: Lane): Seg[] {
    return this.into.get(lane.id) ?? [];
  }

  /** Cars on connectors across `node` (a junction's box). */
  inJunction(node: string): number {
    let n = 0;
    for (const c of this.conns.values()) if (c.node === node) n += c.cars.length;
    return n;
  }
}

/** An obstacle cars brake for (the player): position (x, z). */
export interface Obstacle {
  x: number;
  z: number;
}

export class TrafficSim {
  readonly cars: SimCar[] = [];
  private nextId = 1;
  private rand: () => number;
  private dt = 0.05;

  constructor(
    readonly net: TrafficNet,
    seed = 1,
  ) {
    let s = seed >>> 0 || 1;
    this.rand = () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
  }

  /** Put a car on `lane` at `s` if there are 10 m free around it. */
  spawn(lane: Lane, s: number, body: number, color: [number, number, number]): SimCar | null {
    if (lane.cars.some((c) => Math.abs(c.s - s) < 10)) return null;
    const pose = sampleSeg(lane, s, [0, 0, 0, 0]);
    const car: SimCar = { id: this.nextId++, seg: lane, s, v: lane.speed * 0.6, next: null, body, color, waited: 0, cleared: false, blocked: 0, a: 0, pitch: 0, lift: 0, dead: false, prev: [...pose] as SimCar['prev'], cur: [...pose] as SimCar['cur'] };
    insert(lane, car);
    this.cars.push(car);
    return car;
  }

  despawn(car: SimCar): void {
    car.dead = true;
  }

  step(dt: number, obstacles: Obstacle[] = []): void {
    const net = this.net;
    net.time += dt;
    this.dt = dt;
    for (const car of this.cars) {
      if (car.dead) continue;
      const accel = this.accel(car, obstacles);
      car.a = accel;
      car.v = Math.max(0, car.v + accel * dt);
      this.advance(car, car.v * dt);
      car.prev = car.cur;
      car.cur = sampleSeg(car.seg, car.s, [0, 0, 0, 0]);
      slopeAt(car.seg, car.s, car);
    }
    // Drop dead cars.
    for (let i = this.cars.length - 1; i >= 0; i--) {
      const c = this.cars[i];
      if (!c.dead) continue;
      remove(c.seg, c);
      this.cars.splice(i, 1);
    }
  }

  private chooseNext(lane: Lane): Lane | null {
    const exits = this.net.exits(lane);
    if (exits.length === 0) return null;
    // Keep the lane index where the road allows it (main roads have two).
    const same = exits.filter((l) => l.index === lane.index);
    const pool = same.length && this.rand() < 0.7 ? same : exits;
    return pool[Math.floor(this.rand() * pool.length)];
  }

  /** IDM acceleration toward the lane's speed, limited by what's ahead. */
  private accel(car: SimCar, obstacles: Obstacle[]): number {
    const seg = car.seg;
    const lane = seg.lane;
    const v0 = lane ? lane.speed : CAR.turnSpeed;
    let gap = Infinity;
    let vLead = 0;
    /** Held by a red light or a stop sign ahead anyway (then the player isn't what's in the way). */
    let mustStop = !!lane && lane.stopAtEnd && seg.length - car.s < 60 && this.net.hasSignal(lane.end) && this.net.light(lane.end, lane.dir) !== 'green';
    // The next car on this segment.
    const i = seg.cars.indexOf(car);
    const ahead = seg.cars[i + 1];
    if (ahead) {
      gap = ahead.s - car.s - CAR.length;
      vLead = ahead.v;
    } else {
      const rest = seg.length - car.s;
      // Look into the next segments (connector, lane).
      if (lane && !car.next) car.next = this.chooseNext(lane);
      const nexts: Seg[] = [];
      if (lane && car.next) {
        const conn = this.net.connector(lane, car.next);
        nexts.push(conn, car.next);
      } else if (seg.to) nexts.push(seg.to);
      let before = rest;
      for (const n of nexts) {
        const first = n.cars[0];
        if (first) {
          gap = before + first.s - CAR.length;
          vLead = first.v;
          break;
        }
        before += n.length;
        if (before > 80) break;
      }
      // Merging: yield to cars on other connectors into the same lane that will get there first.
      const target = lane ? car.next : (seg.to ?? null);
      if (target) {
        const mine = lane && car.next ? rest + this.net.connector(lane, car.next).length : rest;
        for (const other of this.net.mergingInto(target)) {
          if (other === seg) continue;
          for (const c2 of other.cars) {
            const theirs = other.length - c2.s;
            if (theirs < mine) {
              const g = mine - theirs - CAR.length;
              if (g < gap) {
                gap = g;
                vLead = c2.v;
              }
            }
          }
        }
      }
      // Stop line at a junction.
      if (lane && lane.stopAtEnd) {
        const toLine = rest - 0.5;
        if (this.net.hasSignal(lane.end)) {
          const st = this.net.light(lane.end, lane.dir);
          const canStop = toLine > (car.v * car.v) / (2 * CAR.brake) + 1;
          if (st !== 'green' && toLine < 60) mustStop = true;
          if (st === 'red' || (st === 'yellow' && canStop)) {
            if (toLine < gap) {
              gap = toLine;
              vLead = 0;
            }
          }
        } else if (!car.cleared) {
          // All-way stop: stop, wait a moment, go when the junction is clear.
          if (toLine < 3 && car.v < 0.5) {
            car.waited += this.dt;
            if (car.waited > 1 && this.net.inJunction(lane.end) === 0) car.cleared = true;
          }
          if (!car.cleared && toLine < 40) mustStop = true;
          if (!car.cleared && toLine < gap) {
            gap = toLine;
            vLead = 0;
          }
        }
      }
    }
    // Obstacles ahead in the car's path (1.8 m either side, within 30 m).
    let byObstacle = false;
    const h = car.cur[3];
    const fx = Math.cos(h);
    const fz = -Math.sin(h);
    for (const o of obstacles) {
      const rx = o.x - car.cur[0];
      const rz = o.z - car.cur[2];
      const along = rx * fx + rz * fz;
      const side = Math.abs(-rx * fz + rz * fx);
      if (along > 0 && along < 30 && side < 1.8) {
        const g = along - 3;
        if (g < gap) {
          gap = g;
          vLead = 0;
          byObstacle = true;
        }
      }
    }
    const v = car.v;
    car.blocked = byObstacle && !mustStop && v < 1.5 && gap < 12 ? car.blocked + this.dt : 0;
    const free = 1 - Math.pow(v / Math.max(0.1, v0), 4);
    if (!Number.isFinite(gap)) return CAR.accel * free;
    const sStar = CAR.minGap + v * CAR.headway + (v * (v - vLead)) / (2 * Math.sqrt(CAR.accel * CAR.brake));
    const a = CAR.accel * (free - Math.pow(Math.max(0, sStar) / Math.max(0.1, gap), 2));
    return Math.max(-9, a);
  }

  private advance(car: SimCar, ds: number): void {
    car.s += ds;
    // Keep the per-segment order (cars never overtake, but s changed).
    resort(car.seg);
    while (car.s > car.seg.length && !car.dead) {
      const over = car.s - car.seg.length;
      const seg = car.seg;
      let next: Seg | null = null;
      if (seg.lane) {
        if (!car.next) car.next = this.chooseNext(seg.lane);
        if (car.next) next = this.net.connector(seg.lane, car.next);
      } else if (seg.to) {
        next = this.net.lanes.has(seg.to.id) ? seg.to : null;
        car.next = null;
        car.cleared = false;
        car.waited = 0;
      }
      remove(seg, car);
      if (!next) {
        car.dead = true;
        return;
      }
      car.seg = next;
      car.s = over;
      insert(next, car);
    }
  }
}

function insert(seg: Seg, car: SimCar): void {
  let i = 0;
  while (i < seg.cars.length && seg.cars[i].s < car.s) i++;
  seg.cars.splice(i, 0, car);
}

function remove(seg: Seg, car: SimCar): void {
  const i = seg.cars.indexOf(car);
  if (i >= 0) seg.cars.splice(i, 1);
}

function resort(seg: Seg): void {
  const c = seg.cars;
  for (let i = 1; i < c.length; i++) {
    let j = i;
    while (j > 0 && c[j - 1].s > c[j].s) {
      const t = c[j];
      c[j] = c[j - 1];
      c[j - 1] = t;
      j--;
    }
  }
}
